/* eslint-disable react/jsx-props-no-spreading */

import { FormProvider, useForm } from 'react-hook-form';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { defaultPreviewStatusMap, useAdminAssignmentsStore } from '../state/adminAssignmentsStore';

import AssignmentAudienceForm from '../builder/forms/audience/AssignmentAudienceForm';
import AssignmentAutomationsForm from '../builder/forms/automations/AssignmentAutomationsForm';
import AssignmentContentForm from '../builder/forms/AssignmentContentForm';
import AssignmentDetailsForm from '../builder/forms/AssignmentDetailsForm';
import AssignmentNotificationsForm from '../builder/forms/notifications/AssignmentNotificationsForm';
import AssignmentPublishForm from '../builder/forms/AssignmentPublishForm';
import AssignmentSettingsForm from '../builder/forms/AssignmentSettingsForm';
import AssignmentsNavigationBlocker from '../AssignmentsNavigationBlocker';
import AudienceFormPreviewWrapper from '../builder/previews/previewWrappers/AudienceFormPreviewWrapper';
import AutomationsFormPreviewWrapper from '../builder/previews/previewWrappers/AutomationsFormPreviewWrapper';
import ContentFormPreviewWrapper from '../builder/previews/previewWrappers/ContentFormPreviewWrapper';
import EditAssignmentForm from '../builder/EditAssignmentForm';
import ErrorAlert from '@/client/components/data-display/ErrorAlert';
import Loading from '@/client/components/media/Loading';
import NotificationsFormPreviewWrapper from '../builder/previews/previewWrappers/NotificationsFormPreviewWrapper';
import SettingsFormPreviewWrapper from '../builder/previews/previewWrappers/SettingsFormPreviewWrapper';
import type { Step } from '../state/adminAssignmentsStore';
import { WorkflowType } from '@/client/services/api/graphql/gql/graphql';
import { assignmentFormDefaultValues } from '../types/assignmentTypes';
import { useAuthStore } from '@/client/services/state/authStore';
import useDocumentTitle from '@/client/utils/useDocumentTitle';
import { useEffect } from 'react';
import { useGetAssignmentFormEditData } from '../hooks/useGetAssignmentFormEditData';
import { useTranslation } from 'react-i18next';

export default function EditAssignment() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { role, is_manager: isManager } = user;

  useDocumentTitle(
    id ? t('admin.assignments.pageTitle.edit') : t('admin.assignments.pageTitle.create'),
  );
  const location = useLocation();
  const {
    setSteps,
    setStepIndex,
    setPreviewStatusMap,
    setSettingsFormVisited,
    setNotificationsFormVisited,
    setAudienceFormVisited,
    canLeave,
    setCanLeave,
  } = useAdminAssignmentsStore();

  const isCreate = location.pathname.includes('create');
  const isInstance = location.pathname.includes('instance');
  const isEdit = location.pathname.includes('edit');

  useEffect(() => {
    setCanLeave(false);

    setPreviewStatusMap({
      ...defaultPreviewStatusMap,
      generalDetails: isCreate ? 'inProgress' : 'notStarted',
    });

    return () => {
      setSettingsFormVisited(false);
      setNotificationsFormVisited(false);
      setAudienceFormVisited(false);
    };
  }, []);

  const methods = useForm({
    defaultValues: assignmentFormDefaultValues,
    mode: 'onChange',
  });

  const { watch, reset } = methods;

  const { simpleType, settings } = watch();

  const { type } = settings;

  const { data, isFetching, isError } = useGetAssignmentFormEditData(reset);

  const stepOptions: Step[] = [
    {
      label: t('admin.assignments.step.audience'),
      value: 'audience',
      formComponent: <AssignmentAudienceForm />,
      previewComponent: <AudienceFormPreviewWrapper />,
      isDisabled: false,
    },
    {
      label: t('admin.assignments.step.content'),
      value: 'content',
      formComponent: <AssignmentContentForm />,
      previewComponent: <ContentFormPreviewWrapper />,
      isDisabled: false,
    },
    {
      label: t('admin.assignments.step.settings'),
      value: 'settings',
      formComponent: <AssignmentSettingsForm />,
      previewComponent: <SettingsFormPreviewWrapper />,
      isDisabled: false,
    },
    {
      label: t('admin.assignments.step.notifications'),
      value: 'notifications',
      formComponent: <AssignmentNotificationsForm />,
      previewComponent: <NotificationsFormPreviewWrapper />,
      isDisabled: false,
    },
    {
      label: t('admin.assignments.step.publish'),
      value: 'publish',
      formComponent: <AssignmentPublishForm />,
      previewComponent: <div>Publish Preview</div>,
      isDisabled: false,
    },
  ];

  useEffect(() => {
    if (!id && !isInstance) {
      stepOptions.unshift({
        label: t('admin.assignments.step.details'),
        value: 'details',
        formComponent: <AssignmentDetailsForm />,
        previewComponent: <div>Details Preview</div>,
        isDisabled: false,
      });
    }

    const automationsStep = {
      formComponent: <AssignmentAutomationsForm />,
      previewComponent: <AutomationsFormPreviewWrapper />,
      label: t('admin.assignments.step.automations'),
      value: 'automations',
      isDisabled: false,
    };

    if (simpleType === 'automated')
      if (!id) {
        stepOptions.splice(1, 0, automationsStep);
      } else {
        stepOptions.unshift(automationsStep);
      }

    setSteps(stepOptions);
    setStepIndex(0);
  }, [simpleType]);

  if (isFetching) return <Loading />;

  if (isError)
    return (
      <ErrorAlert
        title={t('admin.assignments.error')}
        backAction={{
          to: '/admin/assignments/overview',
          label: t('admin.assignments.error.linkText'),
        }}
      />
    );

  if (isEdit && !isInstance && data?.settings.type === WorkflowType.PointInTime)
    return <Navigate to={`/admin/assignments/details/${id}`} replace />;

  if (isManager && role !== 'admin' && type !== WorkflowType.PointInTime)
    return <Navigate to="/explore" replace />;

  return (
    <>
      <AssignmentsNavigationBlocker enabled={!canLeave} />

      <FormProvider {...methods}>
        <form>
          <EditAssignmentForm />
        </form>
      </FormProvider>
    </>
  );
}
